import { parseLocalDateString, getLocalDateString } from './dateUtils';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
/** Gaps longer than this between activity create separate active periods. */
export const INACTIVE_GAP_DAYS = 45;
/** Overlapping or nearby intervals within this window merge into one period. */
export const MERGE_ADJACENT_DAYS = 14;

function parseDateValue(value) {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return parseLocalDateString(value);
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function minDate(dates) {
  const valid = dates.filter(Boolean);
  if (!valid.length) return null;
  return valid.reduce((earliest, current) => (current < earliest ? current : earliest));
}

function maxDate(dates) {
  const valid = dates.filter(Boolean);
  if (!valid.length) return null;
  return valid.reduce((latest, current) => (current > latest ? current : latest));
}

function daysBetween(start, end) {
  return Math.round((end.getTime() - start.getTime()) / MS_PER_DAY);
}

/**
 * Each sessions row is a recurring slot (or a one-day temporary session).
 * Group by valid_from so overlapping generations merge correctly. Open-ended rows
 * extend to today only on the latest open-ended generation — older generations
 * with stale null rows fall back to their explicit end dates.
 */
function collectSessionIntervals(client, today) {
  const sessions = client.sessions || client.sessionHistory || [];
  if (!sessions.length) return [];

  const groups = new Map();

  sessions.forEach((session) => {
    const start = parseDateValue(session.valid_from);
    if (!start) return;

    const groupKey = getLocalDateString(start);
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey).push(session);
  });

  const isActive = client.status === 'Active';

  const openEndedGroupKeys = [...groups.entries()]
    .filter(([, rows]) => rows.some((row) => !row.valid_until))
    .map(([groupKey]) => groupKey)
    .sort();
  const latestOpenEndedKey = openEndedGroupKeys[openEndedGroupKeys.length - 1] || null;

  const intervals = [];

  groups.forEach((rows, groupKey) => {
    const start = parseLocalDateString(groupKey);
    const explicitEnds = rows
      .map((row) => parseDateValue(row.valid_until))
      .filter(Boolean);
    const hasOpenEnded = rows.some((row) => !row.valid_until);

    if (hasOpenEnded && isActive && groupKey === latestOpenEndedKey) {
      intervals.push({ start, end: today });
      return;
    }

    if (explicitEnds.length > 0) {
      intervals.push({ start, end: maxDate(explicitEnds) });
    }
  });

  return intervals;
}

/**
 * Open-ended client_services often span inactive gaps (pricing_valid_until not set on inactive).
 * Only use service rows that have an explicit end date, or a single open row when no sessions exist.
 */
function collectServiceIntervals(client, today) {
  const services = client.client_services || [];
  const hasSessionHistory = (client.sessions || client.sessionHistory || []).some(
    (session) => session.valid_from
  );

  if (hasSessionHistory) {
    return services
      .map((service) => {
        const start = parseDateValue(service.pricing_valid_from);
        const end = parseDateValue(service.pricing_valid_until);
        if (!start || !end) return null;
        return { start, end };
      })
      .filter(Boolean);
  }

  return services
    .map((service) => {
      const start = parseDateValue(service.pricing_valid_from);
      if (!start) return null;

      let end = parseDateValue(service.pricing_valid_until);
      if (!end) {
        end = client.status === 'Active' ? today : null;
      }

      if (!end) return null;
      return { start, end };
    })
    .filter(Boolean);
}

function clusterAttendancePeriods(dateStrings, gapDays = INACTIVE_GAP_DAYS) {
  if (!dateStrings?.length) return [];

  const sorted = [...new Set(dateStrings)].sort();
  const periods = [{ start: sorted[0], end: sorted[0] }];

  for (let i = 1; i < sorted.length; i += 1) {
    const current = parseLocalDateString(sorted[i]);
    const lastEnd = parseLocalDateString(periods[periods.length - 1].end);
    const gap = daysBetween(lastEnd, current);

    if (gap > gapDays) {
      periods.push({ start: sorted[i], end: sorted[i] });
    } else {
      periods[periods.length - 1].end = sorted[i];
    }
  }

  return periods.map((period) => ({
    start: parseLocalDateString(period.start),
    end: parseLocalDateString(period.end),
  }));
}

/**
 * Merge overlapping intervals; split when gap exceeds inactiveGapDays.
 */
export function mergeActivePeriods(intervals, inactiveGapDays = INACTIVE_GAP_DAYS) {
  if (!intervals.length) return [];

  const sorted = [...intervals].sort((a, b) => a.start - b.start);
  const merged = [{ start: sorted[0].start, end: sorted[0].end }];

  for (let i = 1; i < sorted.length; i += 1) {
    const current = sorted[i];
    const last = merged[merged.length - 1];
    const gap = daysBetween(last.end, current.start);

    if (gap <= inactiveGapDays || current.start <= last.end) {
      last.end = current.end > last.end ? current.end : last.end;
    } else {
      merged.push({ start: current.start, end: current.end });
    }
  }

  return merged;
}

/**
 * Derive active stints from services, sessions, and attendance gaps.
 */
export function getClientActivePeriods(client, attendanceData = {}, today = new Date()) {
  const todayNormalized = new Date(today);
  todayNormalized.setHours(0, 0, 0, 0);

  const sessionIntervals = collectSessionIntervals(client, todayNormalized);
  const serviceIntervals = collectServiceIntervals(client, todayNormalized);
  const attendancePeriods = clusterAttendancePeriods(attendanceData.presentDates);

  // Session rows define schedule stints; mixing attendance can bridge inactive gaps.
  const intervals =
    sessionIntervals.length > 0
      ? sessionIntervals
      : [...serviceIntervals, ...attendancePeriods];

  if (!intervals.length) {
    const attendanceStart = parseDateValue(attendanceData.firstPresentDate);
    const attendanceEnd = parseDateValue(attendanceData.lastPresentDate);
    if (attendanceStart && attendanceEnd) {
      intervals.push({ start: attendanceStart, end: attendanceEnd });
    }
  }

  return mergeActivePeriods(intervals);
}

export function sumPeriodTenureDays(periods) {
  return periods.reduce((total, period) => total + getClientTenureDays(period.start, period.end), 0);
}

/**
 * @deprecated Use getClientActivePeriods — kept for tests
 */
export function getClientStartDate(client, firstPresentDate = null) {
  const periods = getClientActivePeriods(client, { firstPresentDate, presentDates: firstPresentDate ? [firstPresentDate] : [] });
  return periods.length ? periods[0].start : null;
}

/**
 * @deprecated Use getClientActivePeriods — kept for tests
 */
export function getClientEndDate(client, lastPresentDate = null) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const periods = getClientActivePeriods(client, { lastPresentDate, presentDates: lastPresentDate ? [lastPresentDate] : [] });
  if (!periods.length) return client.status === 'Active' ? today : parseDateValue(lastPresentDate) || today;
  return periods[periods.length - 1].end;
}

export function getClientTenureDays(startDate, endDate) {
  if (!startDate || !endDate) return 0;
  return Math.max(0, daysBetween(startDate, endDate));
}

export function formatTenureDuration(days) {
  if (!days || days <= 0) return 'Less than 1 day';

  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  const remainingDays = days % 30;

  const parts = [];
  if (years > 0) parts.push(`${years} yr${years === 1 ? '' : 's'}`);
  if (months > 0) parts.push(`${months} mo${months === 1 ? '' : 's'}`);
  if (parts.length === 0 && remainingDays > 0) {
    parts.push(`${remainingDays} day${remainingDays === 1 ? '' : 's'}`);
  }

  return parts.join(' ');
}

export function formatPeriodsTooltip(periods) {
  return periods
    .map(
      (period, index) =>
        `Stint ${index + 1}: ${period.start.toLocaleDateString()} – ${period.end.toLocaleDateString()}`
    )
    .join(' · ');
}

export function buildLongevityEntries(clients, attendanceBounds = {}) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return clients
    .map((client) => {
      const attendanceData = attendanceBounds[client.id] || {};
      const periods = getClientActivePeriods(client, attendanceData, today);

      if (!periods.length) {
        return null;
      }

      const startDate = periods[0].start;
      const endDate = periods[periods.length - 1].end;
      const tenureDays = sumPeriodTenureDays(periods);
      const hasReturned = periods.length > 1;

      return {
        client,
        periods,
        startDate,
        endDate,
        startDateStr: getLocalDateString(startDate),
        endDateStr: getLocalDateString(endDate),
        tenureDays,
        tenureLabel: formatTenureDuration(tenureDays),
        hasReturned,
        periodsTooltip: formatPeriodsTooltip(periods),
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.tenureDays - a.tenureDays || a.client.name.localeCompare(b.client.name));
}

export function getTimelineRange(entries) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (!entries.length) {
    const start = new Date(today);
    start.setFullYear(start.getFullYear() - 1);
    return { start, end: today };
  }

  const allStarts = entries.flatMap((entry) => entry.periods.map((period) => period.start));
  const start = minDate(allStarts);
  const end = today;

  const paddedStart = new Date(start);
  paddedStart.setMonth(0, 1);
  paddedStart.setHours(0, 0, 0, 0);

  return { start: paddedStart, end };
}

export function getTimelinePosition(date, rangeStart, rangeEnd) {
  const totalMs = rangeEnd.getTime() - rangeStart.getTime();
  if (totalMs <= 0) return 0;
  const offsetMs = date.getTime() - rangeStart.getTime();
  return Math.min(100, Math.max(0, (offsetMs / totalMs) * 100));
}

export function getYearMarkers(rangeStart, rangeEnd) {
  const markers = [];
  const cursor = new Date(rangeStart);
  cursor.setMonth(0, 1);

  while (cursor <= rangeEnd) {
    markers.push({
      year: cursor.getFullYear(),
      position: getTimelinePosition(cursor, rangeStart, rangeEnd),
    });
    cursor.setFullYear(cursor.getFullYear() + 1);
  }

  return markers;
}
